// @intl project:microsite

import React, { MouseEvent, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { List } from 'immutable'
import { isEmpty } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import {
  clearStorageDataOnLogout,
  COMMON_ENTRY_POINTS,
  COMMON_MIXPANEL_EVENTS,
  COMMON_MIXPANEL_PAGE,
  COMMON_MIXPANEL_PROPERTIES,
  DirectLinkSource,
  FROM_URL_REDIRECT_KEY,
  INTEGRATED_BENEFITS_EVENTS,
  LOGIN_EVENTS,
  MIXPANEL_EVENT_ATTRIBUTES,
  NavBarProps,
  ONBOARDING_WELLNESS_GOALS_PATH,
  PAGE_ROUTES,
  PATHWAYS_EVENTS,
  REFERRAL_ENTRY_POINTS,
  LOCAL_STORAGE_KEY as SHARED_APP_LOGIC_LOCAL_STORAGE_KEY,
  useFlags,
} from '@lyrahealth-inc/shared-app-logic'
import { UTM_SOURCE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/utm'
import { useIsMobile } from '@lyrahealth-inc/ui-core'
import {
  Header as AppHeader,
  ProductTypeContext,
  toJS,
  useStorageWithExpiration,
} from '@lyrahealth-inc/ui-core-crossplatform'
import { HeaderNavMenuProps } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/headerNavMenu/HeaderNavMenu'
import { benefitsHeaderText } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/integratedBenefitsPage/IntegratedBenefitsPage'

import styles from './header.module.scss'
import MobileHeader from './MobileHeader'
import NavBarHeader from './NavBarHeader'
import OnboardingNavBar from './OnboardingNavBar'
import { setToastContent } from '../../../data/appGlobals/appGlobalsActions'
import {
  getBeforeLaunch,
  getCustomerName,
  getCustomerWorkLifeBenefits,
  getHideExtraHeaderOptions,
  getIsClientHPI,
  getLibraryDisabled,
} from '../../../data/customer/customerSelectors'
import { resetHealthPlan, resetTriage, setIsLogoutInProgress } from '../../../data/logout/logoutActions'
import * as MixpanelActions from '../../../data/mixpanel'
import { MIXPANEL_EVENTS, trackEventWithObj } from '../../../data/mixpanel'
import mixpanel from '../../../data/mixpanel/mixpanel-browser'
import { convertRouteToMixpanelPage } from '../../../data/mixpanel/utils'
import {
  getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
  getIsUserInternational,
  getShouldShowAccountBadge,
} from '../../../data/user/userSelectors'
import { isPathInNeurodiversityFlow } from '../../../features/centerOfExcellence/centerOfExcellenceUtils'
import { isInLoginFlow } from '../../../features/login/loginUtils'
import MessagesPopover from '../../../features/lyraTherapy/messagesPopover/MessagesPopover'
import { clearDirectLinkSource, clearWellnessGoals } from '../../../features/onboard/data/onboardDataActions'
import { getOnboardDirectLinkSource } from '../../../features/onboard/data/onboardSelectors'
import {
  HPI_ROUTES,
  PROFILE_CONFIRMATION_PAGES,
  PROFILE_EDIT_PAGES,
} from '../../../features/onboard/data/page-navigation/location-actions'
import { useFavoritedProvidersEntrypointNav } from '../../../features/onboard/favoritedProviders/hooks/useFavoritedProvidersEntrypointNav'
import { isInRegisterFlow, isPathInAutoEnrollmentFlow } from '../../../features/register/data/registerUtils'
import { clearTriage } from '../../../features/triage/data/triageActions'
import {
  clearWellnessCheckInFormValues,
  clearWellnessCheckInResults,
} from '../../../features/wellnessCheckIn/data/wellnessCheckInActions'
import { LOCAL_STORAGE_KEY } from '../../constants/appConstants'
import {
  AUTO_ENROLLMENT_PATHWAYS_PATH,
  PARENT_LED_TEEN_WELCOME_PAGE_PATH,
  REQUIRE_ELIGIBILITY_FORM_PATH,
} from '../../constants/registrationFormConstants'
import { withTrackIntegratedBenefits } from '../../hocs/withTrackIntegratedBenefits'
import { useCareNavigatorModal } from '../../hooks/useCareNavigatorModal'
import { useDefaultMixpanelPage } from '../../hooks/useDefaultMixpanelPage'
import { useReferral } from '../../hooks/useReferral'
import { broadcastSessionTermination } from '../../hooks/useSessionSync'
import { rerouteFromMicrosite } from '../../utils/micrositeUtils'
import { clearPendoSession } from '../../utils/pendoLoader'
import { updateStoredProviderResultsSurveyValues } from '../../utils/pendoUtils'
import { getAssignmentsRoute, getCareInitialRoute } from '../../utils/routingUtils'
import { matchPaths } from '../../utils/stringUtils'
import { hasRole, toggleIsSessionTimerDisabled } from '../../utils/userUtils'

export const Header = ({
  user,
  videoSessionOpen,
  isLyraTherapy,
  appointmentSize,
  libraryDisabled,
  beforeLaunch,
  isUserInternational,
  resetHealthPlan,
  resetTriage,
  isClientHPI,
  trackEventWithObj,
  customerName,
  isUserAssumedInternational,
  trackIntegratedBenefits,
  setToastContent,
  shouldShowAccountBadge,
  currentDirectPathLinkSource,
  clearWellnessCheckInFormValues,
  clearWellnessCheckInResults,
  setIsLogoutInProgress,
  clearWellnessGoals,
  clearDirectLinkSource,
  clearTriage,
}: $TSFixMe) => {
  const intl = useIntl()
  const {
    breakpoints: { isMobileSized, isMinWidthLaptop, isMinWidthTablet },
    colors,
  } = useTheme()
  const navigate = useNavigate()
  const { pathname, search, state } = useLocation()
  const {
    isLanguageSelectorEnabled,
    isDisplayLanguageEnabled,
    shouldShowCareNavigatorHeaderEntrypoint,
    isReferralEnabled,
    shouldShowCareTabInitial,
    adultTriageDisabled,
    psaBanner,
    is1LyraEnabledForCareRoutes,
    isUngatedEssentialsEventsEnabled,
  } = useFlags()
  const { isLyraSelect } = useContext(ProductTypeContext)
  const { careNavigatorModal, careNavigatorEntryPoint } = useCareNavigatorModal({
    clickEntryPoint: COMMON_MIXPANEL_PAGE.GLOBAL_NAV_CARE_NAVIGATOR,
  })
  const favoritedProvidersEntrypointNavProps = useFavoritedProvidersEntrypointNav()
  const { shareReferral } = useReferral()
  // Disable account badge animation after the first visit for some time.
  // The badge state should be specific to each user account, but this uses LocalStorage which is shared across users.
  // This is not ideal, but animation behavior is not critical and we don't have a way to store this per-user.
  const [animateAccountBadge, setAnimateAccountBadge] = useStorageWithExpiration(
    LOCAL_STORAGE_KEY.ANIMATE_MENU_BADGE,
    true,
    20 * 60 * 1000 /* 20 minutes */,
  )
  const mixpanelPage = useDefaultMixpanelPage()
  const [isMobile] = useIsMobile(Number(styles.x_tablet_min_width))
  const pressableRef = useRef<View>(null)
  const queryParams = new URLSearchParams(search)
  const showReferral = isReferralEnabled && !adultTriageDisabled

  const activeTabOverride = queryParams.get('activeTabOverride')
  const cameFromUrlRedirect = queryParams.get(FROM_URL_REDIRECT_KEY)
  const isLoggedIn = !isEmpty(user)
  const pageName = useMemo(() => convertRouteToMixpanelPage(pathname), [pathname])
  const { shouldShowFavoritedProvidersStarIcon, onFavoritedProvidersStarIconPressed } =
    favoritedProvidersEntrypointNavProps
  const isCarePage = customerName === 'care'
  const isFindEmployerPage = isCarePage && pathname === '/'
  const isRegistrationOnboardingPage = pathname.includes(PAGE_ROUTES.REGISTRATION_ONBOARDING)
  const isInternational = isLoggedIn ? isUserInternational : isUserAssumedInternational
  const isViewingEssentialsUngatedContent =
    window.location.pathname.startsWith(`${PAGE_ROUTES.ESSENTIALS_ARTICLES}`) ||
    (window.location.pathname.startsWith(`${PAGE_ROUTES.ESSENTIALS_EVENTS}`) && isUngatedEssentialsEventsEnabled)
  const isHomePage = ['/'].includes(pathname)
  const isLoginPage = isInLoginFlow(pathname)
  const isUnsubscribe = pathname.includes(PAGE_ROUTES.UNSUBSCRIBE)
  const shouldHeaderOverlapPageContent = isHomePage || beforeLaunch || isFindEmployerPage
  const shouldShowCareNavigatorHeaderEntrypointLoggedOut = isHomePage && !beforeLaunch && !isCarePage
  const shouldDefaultHeaderBeOpaque = [
    PAGE_ROUTES.CARE_TEAM_PHONE_NUMBERS,
    PAGE_ROUTES.FORGOT_PASSWORD,
    PAGE_ROUTES.RESET_PASSWORD,
    PAGE_ROUTES.PATHWAYS.ESSENTIALS_HOMEPAGE_WIDGET,
  ].includes(pathname)
  const psaBannerHasDisabledLoginAndRegistration = psaBanner?.enabled ? psaBanner.disableLoginAndRegistration : false
  const shouldHideLoginAndSignUpButtons =
    [
      PAGE_ROUTES.LOGIN,
      PAGE_ROUTES.WORKLIFE_SERVICES,
      PAGE_ROUTES.UNSUBSCRIBE,
      PAGE_ROUTES.VERIFY_ATTESTATION,
      PAGE_ROUTES.FORGOT_PASSWORD,
      PAGE_ROUTES.RESET_PASSWORD,
      PAGE_ROUTES.WORKLIFE_SERVICES_CONTACT_CARE_INTERNATIONAL,
      PAGE_ROUTES.CARE_TEAM_PHONE_NUMBERS,
      PAGE_ROUTES.REGISTRATION_ONBOARDING,
    ].includes(pathname) ||
    beforeLaunch ||
    isCarePage ||
    isViewingEssentialsUngatedContent ||
    psaBannerHasDisabledLoginAndRegistration ||
    isInRegisterFlow(pathname)
  const shouldShowLTHeader = isLyraTherapy
  /** Logged in (and has completed registration) */
  const shouldShowLoggedInHeader = isLoggedIn && !isRegistrationOnboardingPage
  const currentPathname = activeTabOverride ?? pathname
  const pathsWithMobileHeader = [
    '/care-team-phone-numbers',
    '/secure/profile/update-location',
    '/secure/profile/update-notification-settings',
    '/secure/profile/update-personal-information',
    '/secure/profile/update-child-information',
    PAGE_ROUTES.WORKLIFE_SERVICES_CONTACT_CARE_INTERNATIONAL,
    PAGE_ROUTES.PROFILE.DEFAULT,
    PAGE_ROUTES.PROFILE.ELIGIBILITY,
    PAGE_ROUTES.PROFILE.SETTINGS,
    PAGE_ROUTES.PROFILE.SESSION_HISTORY,
    PAGE_ROUTES.PROFILE.HEALTH_PLAN_AND_PAYMENTS,
    PAGE_ROUTES.PROFILE.PRIVACY_AND_LEGAL,
  ]

  useEffect(() => {
    if (pathname === '/' && !isLoggedIn) {
      resetTriage()
    }
  }, [isLoggedIn, pathname, resetTriage])

  useEffect(() => {
    if (search.includes('utm_medium=tbd')) {
      shareReferral({
        source: UTM_SOURCE.WELCOME_EMAIL,
        entryPoint: REFERRAL_ENTRY_POINTS.WELCOME_EMAIL,
      })
    }
  }, [customerName, intl.formatMessage, isMobileSized, search, setToastContent, shareReferral, trackEventWithObj])

  const handleLogin = () => {
    trackEventWithObj({
      event: LOGIN_EVENTS.CLICK_LOGIN,
      ...mixpanelPage,
    })
    navigate(PAGE_ROUTES.LOGIN)
  }

  const handleLogout = () => {
    setIsLogoutInProgress(true)
    trackEventWithObj({
      event: LOGIN_EVENTS.CLICK_LOG_OUT,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
    })
    mixpanel.reset()
    resetHealthPlan()
    clearWellnessCheckInFormValues()
    clearWellnessCheckInResults()
    clearWellnessGoals()
    toggleIsSessionTimerDisabled(false)
    clearPendoSession()
    updateStoredProviderResultsSurveyValues()
    localStorage.removeItem(SHARED_APP_LOGIC_LOCAL_STORAGE_KEY.TRANSFER_ACCOUNT)
    clearStorageDataOnLogout()
    broadcastSessionTermination()

    window.location.href = '/logout'
  }

  const handleProfile = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    trackEventWithObj({
      event: COMMON_MIXPANEL_EVENTS.CLICK_ACCOUNT,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
      [COMMON_MIXPANEL_PROPERTIES.NOTIFICATION_BADGE_SHOWN]: shouldShowAccountBadge,
    })
    navigate(PAGE_ROUTES.PROFILE.DEFAULT, {
      state: { mixpanelEntryPoint: MIXPANEL_EVENT_ATTRIBUTES.ENTRY_POINT.NAV_BAR },
    })
  }

  const handleBenefitsClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    trackEventWithObj({
      event: COMMON_MIXPANEL_EVENTS.CLICK_MY_BENEFITS,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
    })
    trackIntegratedBenefits({
      event: INTEGRATED_BENEFITS_EVENTS.VIEW_INTEGRATED_BENEFITS_PAGE,
      entryPoint: COMMON_MIXPANEL_PAGE.HOME_BASE,
      action: MixpanelActions.MIXPANEL_ACTION.MENU,
    })
    navigate('/secure/index/search-care/benefits')
  }

  const handleMenuBtnClick = () => {
    setAnimateAccountBadge(false)
    trackEventWithObj({
      event: COMMON_MIXPANEL_EVENTS.CLICK_HAMBURGER_MENU,
      [COMMON_MIXPANEL_PROPERTIES.PAGE]: pageName,
      [COMMON_MIXPANEL_PROPERTIES.NOTIFICATION_BADGE_SHOWN]: shouldShowAccountBadge,
    })
  }

  const handleSignUpClick = () => {
    rerouteFromMicrosite({
      user,
      navigate,
      trackEventWithObj,
      mixpanelEvent: PATHWAYS_EVENTS.CLICK_CREATE_ACCOUNT,
      mixpanelPage: mixpanelPage?.page,
    })
  }

  const handleFaqClick = (
    e?: React.MouseEvent<HTMLButtonElement | HTMLButtonElement, MouseEvent> | React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    e?.preventDefault()
    trackEventWithObj({
      event: COMMON_MIXPANEL_EVENTS.CLICK_FAQS,
      [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: COMMON_ENTRY_POINTS.HAMBURGER_MENU,
    })
    if (isLoggedIn) {
      navigate('/faq')
    } else {
      navigate('/#faq')
    }
  }

  const handleReferralClick = (e?: React.MouseEvent<HTMLButtonElement>, fromLogin?: boolean) => {
    e?.preventDefault()

    shareReferral(
      fromLogin
        ? {
            source: UTM_SOURCE.LOGIN_PAGE,
            entryPoint: REFERRAL_ENTRY_POINTS.LOGIN_PAGE,
          }
        : {
            source: UTM_SOURCE.HAMBURGER_MENU,
            entryPoint: REFERRAL_ENTRY_POINTS.HAMBURGER_MENU,
          },
    )
  }

  const handleHomeClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    navigate('/secure/index/search-care')
  }

  const handleCareClick = () => {
    navigate(getAssignmentsRoute(is1LyraEnabledForCareRoutes), {
      state: { [COMMON_MIXPANEL_PROPERTIES.REFERRER_PAGE]: pageName },
    })
  }

  const handleCareEmptyStateClick = () => {
    navigate(getCareInitialRoute(is1LyraEnabledForCareRoutes))
  }

  const handleSessionsClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    navigate('/secure/index/sessions')
  }

  const handleEssentialsClick = (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    navigate(
      `/secure/index/essentials?${COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT}=${MIXPANEL_EVENT_ATTRIBUTES.ENTRY_POINT.LIBRARY}`,
      {
        state: { [COMMON_MIXPANEL_PROPERTIES.REFERRER_PAGE]: pageName },
      },
    )
  }

  const shouldShowEssentialsLT = () => !libraryDisabled

  const shouldShowNotInCareTabs = isLoggedIn && !isCarePage && !isLyraTherapy && !isRegistrationOnboardingPage

  const shouldShowEssentials = () => shouldShowNotInCareTabs && shouldShowEssentialsLT()

  const shouldShowHome = () => shouldShowNotInCareTabs

  const shouldShowSessions = () => shouldShowNotInCareTabs && appointmentSize > 0

  const shouldShowCare = () => shouldShowNotInCareTabs && shouldShowCareTabInitial && !isLyraSelect

  const handleLogoClick = () => {
    const generateLogoHref = () => {
      if (isLoggedIn && hasRole(user, 'ROLE_care_manager')) {
        return '/secure/care-manager/provider-matcher/'
      } else if (isLoggedIn && hasRole(user, 'ROLE_usr')) {
        return '/secure/index/'
      } else {
        return '/'
      }
    }

    trackEventWithObj({
      event: MIXPANEL_EVENTS.CLICK_HEADER_LYRA_LOGO,
      page: convertRouteToMixpanelPage(pathname, undefined, false),
    })

    if (isPathInAutoEnrollmentFlow(pathname)) {
      window.open('/', '_blank')
    } else {
      navigate(generateLogoHref())
    }
    if (pressableRef.current) {
      pressableRef.current.blur()
    }
  }

  const handleLanguageSelectorGetHelpPress = () => {
    trackEventWithObj({ event: MIXPANEL_EVENTS.CLICK_DONT_SEE_YOUR_LANGUAGE })
    navigate('/care-team-phone-numbers')
  }

  const handleSearchPress = () => {
    navigate(PAGE_ROUTES.UNIVERSAL_SEARCH)
  }

  const isNeurodiversityLoggedInLandingPage =
    isLoggedIn && matchPaths(location.pathname, ['/center-of-excellence/neurodiversity'])

  const isNeurodiversitySubpage =
    matchPaths(location.pathname, [
      '/secure/center-of-excellence/neurodiversity/care-options',
      '/secure/center-of-excellence/neurodiversity/contact-care-navigator',
    ]) || isNeurodiversityLoggedInLandingPage

  const isHealthChallengesLoggedInLandingPage = isLoggedIn && matchPaths(location.pathname, ['/health-challenges'])

  const isHealthChallengesToolkitPage =
    matchPaths(location.pathname, ['/secure/health-challenges/care-options']) || isHealthChallengesLoggedInLandingPage

  const isWomensHealthLoggedInLandingPage = isLoggedIn && matchPaths(location.pathname, ['/womens-health'])

  const isWomensHealthToolkitPage =
    matchPaths(location.pathname, ['/secure/womens-health/care-options']) || isWomensHealthLoggedInLandingPage
  const handleNavigateBackFromCOEToolkit = useCallback(() => {
    switch (currentDirectPathLinkSource) {
      case DirectLinkSource.HOMEBASE_WELLNESS_TOOLKIT_CTA:
        navigate('/secure/index/search-care')
        break
      case DirectLinkSource.TOOLKIT_DIRECTORY:
      case DirectLinkSource.CENTER_OF_EXCELLENCE_TOOLKIT:
        navigate(PAGE_ROUTES.TOOLKIT_DIRECTORY)
        break
      case DirectLinkSource.INTEGRATED_BENEFITS_NEURODIVERSITY_TOOLKIT_CTA:
      case DirectLinkSource.INTEGRATED_BENEFITS_WOMENS_HEALTH_TOOLKIT_CTA:
      case DirectLinkSource.INTEGRATED_BENEFITS_HEALTH_CHALLENGES_TOOLKIT_CTA:
        navigate(PAGE_ROUTES.SEARCH_CARE.BENEFITS)
        break
      default:
        navigate(-1)
        break
    }
    clearTriage()
    clearDirectLinkSource()
  }, [clearDirectLinkSource, clearTriage, currentDirectPathLinkSource, navigate])

  const navBarHeaderOnBack = useCallback(() => {
    // if navigated from microsite we should navigate back to homebase
    if (currentDirectPathLinkSource === DirectLinkSource.MICROSITE_FORKED_TRIAGE) {
      navigate('/')
    } else if (cameFromUrlRedirect && isNeurodiversitySubpage) {
      navigate(PAGE_ROUTES.CENTER_OF_EXCELLENCE.NEURODIVERSITY.LANDING_PAGE, { replace: true })
    } else if (
      isNeurodiversityLoggedInLandingPage ||
      isHealthChallengesLoggedInLandingPage ||
      isWomensHealthLoggedInLandingPage
    ) {
      handleNavigateBackFromCOEToolkit()
    } else {
      navigate(-1)
    }
  }, [
    cameFromUrlRedirect,
    currentDirectPathLinkSource,
    handleNavigateBackFromCOEToolkit,
    isWomensHealthLoggedInLandingPage,
    isHealthChallengesLoggedInLandingPage,
    isNeurodiversityLoggedInLandingPage,
    isNeurodiversitySubpage,
    navigate,
  ])

  if (isCarePage && pathname !== '/' && !isLoggedIn) {
    return null
  }

  const isAutoEnrollmentPathwaysPage = pathname === AUTO_ENROLLMENT_PATHWAYS_PATH
  if (isAutoEnrollmentPathwaysPage) {
    return null
  }

  if (videoSessionOpen) {
    return null
  }

  if (
    pathname.includes('satisfaction') ||
    pathname.includes('verify-attestation') ||
    pathname.includes(ONBOARDING_WELLNESS_GOALS_PATH) ||
    pathname.includes(PAGE_ROUTES.TOOLKIT_DIRECTORY)
  ) {
    return null
  }

  // Use container headers for health plan and payment forms
  if (
    HPI_ROUTES.some(
      (hpiRoute) =>
        pathname.includes(hpiRoute) &&
        (pathname.includes(PAGE_ROUTES.PROFILE.COVERAGE_CHECK) ||
          pathname.includes(PAGE_ROUTES.HPI_COVERAGE_CHECK.EMAIL.DEFAULT)),
    )
  ) {
    return null
  }

  if (matchPaths(location.pathname, PROFILE_EDIT_PAGES) || matchPaths(location.pathname, PROFILE_CONFIRMATION_PAGES)) {
    return null
  }

  if (pathname.includes(PAGE_ROUTES.WELLNESS_CHECK_IN.DEFAULT)) {
    return null
  }

  if (isMobile && isViewingEssentialsUngatedContent) {
    return null
  }

  const isBlueBackgroundColor = matchPaths(location.pathname, ['/secure/onboard/summary'])

  // Hide the header for when user is within assignment subpages
  if (!isMinWidthLaptop && pathname.includes('assignments/')) {
    return null
  }

  if (
    matchPaths(location.pathname, [
      '/secure/onboard/about',
      '/secure/onboard/about/:id',
      '/secure/onboard/treatment-options',
      '/secure/index/provider/:id/therapy-care-options',
      '/secure/index/provider/:id/coaching-options',
      '/secure/onboard/match-location',
      '/secure/onboard/provider/:id',
      '/secure/wellness/trauma-checkin/results',
      PAGE_ROUTES.COMING_SOON_MICROSITE,
    ])
  ) {
    // Triage, Treatment recs, and registration onboarding have custom navigation actions, so the navbar is included in the respective containers.
    return null
  } else if (
    pathname.includes('/secure/index/favorited-providers') ||
    (pathname.includes('/secure/index/provider/') && state?.isFavoritedProvidersPageRoute) ||
    pathname.includes('/secure/onboard/') ||
    pathname.includes('/secure/coverage-check/onboard') ||
    pathname.includes('/secure/coverage-check/option') ||
    pathname.includes('/secure/coverage-check/provider') ||
    pathname.endsWith('/secure/wellness/trauma-checkin') ||
    matchPaths(location.pathname, ['/secure/wellness/toolkit/:id'])
  ) {
    const isTraumaCheckin = pathname.endsWith('/secure/wellness/trauma-checkin')
    return (
      <OnboardingNavBar
        favoritedProvidersEntrypointNavProps={favoritedProvidersEntrypointNavProps}
        isStatic={matchPaths(pathname, [
          PAGE_ROUTES.CONTACT_CARE_ADVOCATE_FORM,
          PAGE_ROUTES.CONTACT_CARE_ADVOCATE_CONFIRMATION,
        ])}
        showBackButton={isTraumaCheckin ? false : undefined}
        shouldShowCareNavigatorHeaderEntrypoint={isTraumaCheckin ? false : undefined}
        headerBackgroundColor={
          isLyraSelect && isBlueBackgroundColor ? colors.components.s4cTriageBackgroundHeader.background : undefined
        }
      />
    )
  }

  if (
    pathname.includes(PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE) ||
    pathname.includes(PAGE_ROUTES.PATHWAYS.SELF_CARE) ||
    pathname.includes(PAGE_ROUTES.FAMILY_CARE_OPTIONS) ||
    (pathname.includes(PAGE_ROUTES.SEARCH_CARE.BENEFITS) && isMobileSized) ||
    pathname === REQUIRE_ELIGIBILITY_FORM_PATH ||
    pathname === PARENT_LED_TEEN_WELCOME_PAGE_PATH ||
    isNeurodiversitySubpage ||
    isHealthChallengesToolkitPage ||
    isWomensHealthToolkitPage
  ) {
    const customHeaderProps = {
      [PAGE_ROUTES.SEARCH_CARE.BENEFITS]: {
        showBackButton: true,
        title: benefitsHeaderText,
        backgroundColor: colors.backgroundPrimary,
        shouldShowCareNavigatorHeaderEntrypoint: false,
        borderBottomColor: colors.borderDefault,
        isTransparent: false,
      },
      [REQUIRE_ELIGIBILITY_FORM_PATH]: {
        onExit: handleLogout,
        showBackButton: false,
        favoritedProvidersEntrypointNavProps: undefined,
      },
      [PARENT_LED_TEEN_WELCOME_PAGE_PATH]: {
        onExit: () => navigate(PAGE_ROUTES.SECURE_INDEX),
        showBackButton: false,
        isTransparent: false,
        favoritedProvidersEntrypointNavProps: undefined,
        borderBottomColor: 'transparent',
      },
    }

    const getCustomHeaderProps = (pathname: string): Partial<NavBarProps> => {
      if (isNeurodiversitySubpage || isHealthChallengesToolkitPage || isWomensHealthToolkitPage) {
        return {
          style: { backgroundColor: colors.backgroundSecondary },
          favoritedProvidersEntrypointNavProps: undefined,
          onLogoPress: () => {
            clearTriage()
            clearDirectLinkSource()
            navigate(PAGE_ROUTES.HOMEBASE)
          },
        }
      }
      return customHeaderProps[pathname] || {}
    }

    return (
      <NavBarHeader
        isInternational={isInternational}
        onBack={navBarHeaderOnBack}
        careNavigatorModal={careNavigatorModal}
        careNavigatorEntryPoint={careNavigatorEntryPoint}
        shouldShowCareNavigatorHeaderEntrypoint
        isTransparent
        favoritedProvidersEntrypointNavProps={favoritedProvidersEntrypointNavProps}
        {...getCustomHeaderProps(pathname)}
      />
    )
  }

  if (isMobile && pathsWithMobileHeader.includes(pathname)) {
    return <MobileHeader pathname={pathname} isClientHPI={isClientHPI} />
  }

  const headerNavMenuProps: HeaderNavMenuProps = {
    handleBenefitsPress: handleBenefitsClick,
    handleAccountPress: handleProfile,
    handleFAQsPress: handleFaqClick,
    handleReferralPress: handleReferralClick,
    handleLogOutPress: handleLogout,
    handleSessionsPress: handleSessionsClick,
    handleFavoritedProvidersStarIconPress: onFavoritedProvidersStarIconPressed,
    shouldShowReferral: () => showReferral,
    currentPathname,
    trackMenuClick: handleMenuBtnClick,
    showAccountBadge: shouldShowAccountBadge,
    animateAccountBadge,
    shouldShowSessions: shouldShowLTHeader ? () => true : shouldShowSessions,
    shouldShowFavoritedProvidersStarIcon: () => shouldShowFavoritedProvidersStarIcon,
  }

  const mainNavItemsProps = {
    ...headerNavMenuProps,
    handleHomePress: handleHomeClick,
    handleCarePress: shouldShowLTHeader ? handleCareClick : handleCareEmptyStateClick,
    handleEssentialsPress: handleEssentialsClick,
    shouldShowEssentials: shouldShowLTHeader ? shouldShowEssentialsLT : shouldShowEssentials,
    shouldShowCare: shouldShowLTHeader ? () => true : shouldShowCare,
    shouldShowHome: shouldShowLTHeader ? () => true : shouldShowHome,
  }

  const appHeader = (
    <AppHeader
      shouldShowCareNavigatorHeaderEntrypoint={shouldShowCareNavigatorHeaderEntrypoint}
      careNavigatorModal={careNavigatorModal}
      careNavigatorEntryPoint={careNavigatorEntryPoint}
      headerNavMenuProps={headerNavMenuProps}
      mainNavItemsProps={mainNavItemsProps}
      currentPathname={currentPathname}
      onLogoPressed={handleLogoClick}
      messagesPopover={shouldShowLTHeader && <MessagesPopover shouldUseHeaderStyle />}
      showMessages
      favoritedProvidersEntrypointNavProps={favoritedProvidersEntrypointNavProps}
      shouldShowSearch
      onSearchPressed={handleSearchPress}
    />
  )

  const defaultHeader = (
    <AppHeader
      onLogoPressed={handleLogoClick}
      shouldShowLoginAndSignUpButtons={!shouldHideLoginAndSignUpButtons}
      onLoginPressed={handleLogin}
      onSignUpPressed={handleSignUpClick}
      shouldShowCareNavigatorHeaderEntrypoint={shouldShowCareNavigatorHeaderEntrypointLoggedOut}
      careNavigatorModal={careNavigatorModal}
      careNavigatorEntryPoint={careNavigatorEntryPoint}
      shouldShowReferral={showReferral && !isCarePage && isLoginPage}
      onReferralPressed={() => {
        handleReferralClick(undefined, true)
      }}
      isTransparentBackground={!shouldDefaultHeaderBeOpaque && isMinWidthTablet}
      shouldOverlapPageContent={shouldHeaderOverlapPageContent && isMinWidthTablet}
      shouldShowLanguageSelector={
        (isLanguageSelectorEnabled && (isHomePage || isFindEmployerPage)) ||
        beforeLaunch ||
        (isDisplayLanguageEnabled &&
          ((isLoginPage && !isCarePage) ||
            isRegistrationOnboardingPage ||
            isUnsubscribe ||
            isInRegisterFlow(pathname) ||
            isPathInNeurodiversityFlow(pathname)))
      }
      onLanguageSelectorGetHelpPress={handleLanguageSelectorGetHelpPress}
      beforeLaunch={beforeLaunch}
    />
  )

  return shouldShowLoggedInHeader ? appHeader : defaultHeader
}

const mapStateToProps = ($$state: $TSFixMe) => {
  const customerName = getCustomerName($$state)
  const episodes = $$state.getIn(['lyraTherapy', 'episodes'], List())
  const isLyraTherapy = !episodes.isEmpty()

  return {
    isLyraTherapy,
    appointmentSize: $$state.getIn(['appointmentDashboard', 'appointments']).size ?? 0,
    user: $$state.get('user'),
    videoSessionOpen: $$state.getIn(['lyraTherapy', 'video', 'sessionOpen']),
    worklifeEnabled: !!getCustomerWorkLifeBenefits($$state),
    hideExtraHeaderOptions: getHideExtraHeaderOptions($$state),
    libraryDisabled: getLibraryDisabled($$state),
    beforeLaunch: getBeforeLaunch($$state),
    isUserInternational: getIsUserInternational($$state),
    isClientHPI: getIsClientHPI($$state),
    customerName,
    isUserAssumedInternational: getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer($$state),
    shouldShowAccountBadge: getShouldShowAccountBadge($$state),
    currentDirectPathLinkSource: getOnboardDirectLinkSource($$state),
  }
}

const mapDispatchToProps = {
  trackEventWithObj,
  resetHealthPlan,
  resetTriage,
  setToastContent,
  clearWellnessCheckInFormValues,
  clearWellnessCheckInResults,
  setIsLogoutInProgress,
  clearWellnessGoals,
  clearTriage,
  clearDirectLinkSource,
}

export default connect(mapStateToProps, mapDispatchToProps)(toJS(withTrackIntegratedBenefits(Header)))
